import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, BookOpen, Loader2, AlertTriangle } from 'lucide-react';
import Navigation from '../Navigation';
import CopyButton from '../CopyButton';

// Markdown content will be loaded dynamically

/**
 * AdminGuideViewer Component
 * 
 * Displays individual admin guide content with markdown rendering.
 * Uses the same prose styling as GuideView.jsx for consistency.
 * 
 * Features:
 * - Static markdown file imports
 * - Consistent prose styling with GuideView
 * - Breadcrumb navigation
 * - Back navigation to admin guides
 * - Error handling for missing guides
 */
const AdminGuideViewer = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guideContent, setGuideContent] = useState('');
  const [guideTitle, setGuideTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Guide metadata mapping with static content
  const guideMetadata = {
    'markdown-formatting': {
      title: 'Markdown Formatting Guide',
      content: `# Markdown Conversion Prompt

Use this prompt when you want to convert any document, text, or content into properly formatted markdown for your portal:

---

## **PROMPT TO USE:**

\`\`\`
Please convert the following content into clean, properly formatted markdown suitable 
for a member portal guide. Follow these formatting rules:

1. Use proper heading hierarchy:
   - # for main title (use once at the top)
   - ## for major sections
   - ### for subsections

2. Use **bold** for emphasis and important terms

3. Format lists properly:
   - Use \`-\` for unordered lists
   - Use \`1.\` \`2.\` \`3.\` for ordered lists
   - Indent sub-items with 2 spaces

4. For tables, use proper markdown table syntax with pipes and dashes

5. Keep paragraphs separated by blank lines for readability

6. Use \`inline code\` for technical terms or commands

7. Use code blocks with triple backticks for longer code examples

8. Avoid unnecessary HTML - use markdown syntax only

9. Make sure all formatting is clean and will render well

10. IMPORTANT - Suggest the appropriate category for this resource from:
    - Fundamentals (core concepts and foundational knowledge)
    - Strategy (strategic planning and business development)
    - Community (community building and engagement)
    - Growth (scaling and growth strategies)
    - Templates (downloadable templates and frameworks)
    - Guides (step-by-step guides and playbooks)
    - Tools (software tools and resources)
    - Worksheets (interactive worksheets and exercises)
    - Marketing & Sales (customer acquisition and marketing strategies)
    - Financial Analysis (budget planning, P&L analysis, financial reporting)
    - Financials (fundraising, capital raising, financial planning)
    - Team Management (meetings, performance reviews, leadership)
    - Operations (process optimization, workflow analysis)
    - Product Development (product strategy, roadmapping, features)
    - Customer Research (survey design, feedback analysis, market research)
    - Weekly Training Calls (recordings from group training sessions)

At the end of your response, include:

**Recommended Category:** [Category Name]
**Reason:** [Brief explanation of why this category fits best]

Here's the content to convert:

[PASTE YOUR CONTENT HERE]
\`\`\`

---

# Markdown Cheatsheet for Portal Guides

## Headings

\`\`\`markdown
# H1 Heading - Main Title (Use once at top)
## H2 Heading - Major Section
### H3 Heading - Subsection
\`\`\`

**Visual result:**
- H1 = Very large (4xl), bold
- H2 = Large (2xl), bold, extra top spacing
- H3 = Medium (xl), bold, moderate spacing

---

## Text Formatting

\`\`\`markdown
**Bold text** for emphasis
*Italic text* for subtle emphasis
**Bold and *italic* combined**
\`\`\`

**Result:**
- **Bold text** for emphasis
- *Italic text* for subtle emphasis
- **Bold and *italic* combined**

---

## Lists

### Unordered Lists
\`\`\`markdown
- First item
- Second item
  - Nested item (2 spaces indent)
  - Another nested item
- Third item
\`\`\`

**Result:**
- First item
- Second item
  - Nested item
  - Another nested item
- Third item

### Ordered Lists
\`\`\`markdown
1. First step
2. Second step
3. Third step
   - Sub-point (can mix with unordered)
4. Fourth step
\`\`\`

**Result:**
1. First step
2. Second step
3. Third step
   - Sub-point
4. Fourth step

---

## Links

\`\`\`markdown
[Link text](https://example.com)
[Link with title](https://example.com "This appears on hover")
\`\`\`

**Result:**
[Link text](https://example.com)

---

## Tables

\`\`\`markdown
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |
\`\`\`

**Result:**

| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |

**Tips:**
- Use pipes \`|\` to separate columns
- Use dashes \`---\` in the header separator row
- Alignment doesn't matter in the raw markdown (tables auto-format)
- Your portal will render these with borders and proper styling

---

## Code

### Inline Code
\`\`\`markdown
Use \`inline code\` for commands, variables, or technical terms.
\`\`\`

**Result:** Use \`inline code\` for commands, variables, or technical terms.

### Code Blocks
\`\`\`markdown
\`\`\`javascript
function example() {
  return "Code block with syntax highlighting";
}
\`\`\`
\`\`\`

**Result:**
\`\`\`javascript
function example() {
  return "Code block with syntax highlighting";
}
\`\`\`

---

## Blockquotes

\`\`\`markdown
> This is a quote or important callout
> It can span multiple lines
>
> And have multiple paragraphs
\`\`\`

**Result:**
> This is a quote or important callout
> It can span multiple lines
>
> And have multiple paragraphs

---

## Horizontal Rules

\`\`\`markdown
---
\`\`\`

**Result:**

---

Creates a visual separator between sections.

---

## Paragraphs & Line Breaks

\`\`\`markdown
This is paragraph one.

This is paragraph two (blank line between them).

This is paragraph three.
\`\`\`

**Important:** Always leave blank lines between paragraphs for proper spacing.

---

## Best Practices for Portal Guides

### ✅ DO:
- Use one H1 (\`#\`) for the guide title
- Use H2 (\`##\`) for main sections
- Use H3 (\`###\`) for subsections
- Keep paragraphs short and scannable
- Use bold for key terms and concepts
- Use lists for step-by-step instructions
- Add tables for comparison data
- Include blank lines between sections

### ❌ DON'T:
- Don't use HTML tags (stick to markdown)
- Don't skip heading levels (don't go from H1 to H3)
- Don't forget blank lines between paragraphs
- Don't make paragraphs too long (break them up)
- Don't overuse bold (use sparingly for emphasis)

---

## Example: Well-Formatted Guide

\`\`\`markdown
# Complete Guide Title

**Disclaimer:** Add any important notices at the top.

## Introduction

This is the opening paragraph that sets context. Keep it concise and 
explain what the guide covers.

Second paragraph with more details if needed.

## Main Section

### Subsection One

Content for this subsection with **important terms** in bold.

Key points:
- First point
- Second point
- Third point

### Subsection Two

1. First step
2. Second step
3. Third step

## Comparison Table

| Option A | Option B | Option C |
|----------|----------|----------|
| Feature 1 | Feature 1 | Feature 1 |
| Feature 2 | Feature 2 | Feature 2 |

## Best Practices

Follow these guidelines:

1. Define scope clearly
2. Document everything
3. Seek professional counsel

## Conclusion

Wrap up the guide with key takeaways and next steps.
\`\`\`

---

## Quick Reference

| Element | Markdown Syntax |
|---------|----------------|
| H1 Title | \`# Title\` |
| H2 Section | \`## Section\` |
| H3 Subsection | \`### Subsection\` |
| Bold | \`**text**\` |
| Italic | \`*text*\` |
| List item | \`- item\` or \`1. item\` |
| Link | \`[text](url)\` |
| Inline code | \`\` \`code\` \`\` |
| Code block | \` \`\` code \`\` \` |
| Table | \`\\| col \\| col \\|\` |
| Quote | \`> quote\` |
| Horizontal rule | \`---\` |

---

**Remember:** The portal uses Tailwind Typography for rendering, so all standard markdown will look great with proper spacing, sizing, and styling automatically applied!`
    },
    'managing-resources': {
      title: 'Managing Resources and Guides',
      content: `# Managing Resources and Guides

This guide covers how to create, edit, and organize resources and guides in the IAS Portal admin panel. Resources can be either external links or internal guides with markdown content.

## Understanding Resource Types

### External Links
- Direct users to external websites, documents, or tools
- Include description and optional preview
- Track click-through rates
- Best for: External tools, reference materials, partner resources

### Internal Guides
- Full markdown content displayed within the portal
- Rich formatting with tables, code blocks, and media
- Searchable content
- Best for: Step-by-step tutorials, comprehensive documentation, internal processes

## Creating New Resources

### Step 1: Access Resource Management
1. Navigate to **Admin Dashboard**
2. Click on **"Resources"** tab
3. Click **"Add Resource"** button

### Step 2: Basic Information
Fill in the required fields:

- **Title**: Clear, descriptive title (required)
- **Description**: Brief summary of the resource (required)
- **Content Type**: Choose "Link" or "Guide"
- **Categories**: Select relevant categories (can select multiple)

### Step 3: Content Configuration

#### For External Links:
- **URL**: Complete web address (include https://)
- **Preview Text**: Optional description for link previews
- **Open in New Tab**: Recommended for external links

#### For Internal Guides:
- **Markdown Content**: Use the rich text editor
- **Table of Contents**: Auto-generated from headers
- **Related Resources**: Link to other relevant content

### Step 4: Publishing Options
- **Status**: Draft or Published
- **Featured**: Highlight on main resources page
- **Access Level**: Public or restricted to specific roles

## Content Creation Best Practices

### Writing Effective Descriptions
\`\`\`markdown
✅ Good: "Step-by-step guide for setting up two-factor authentication for enhanced account security"

❌ Poor: "Guide about security"
\`\`\`

### Choosing Categories
- Select 1-3 relevant categories
- Use existing categories when possible
- Create new categories only when necessary
- Consider user workflow and discovery

### Link Resources
- Use descriptive titles that indicate the destination
- Include helpful descriptions
- Test all links before publishing
- Consider mobile accessibility

### Guide Resources
- Start with a clear introduction
- Use proper heading hierarchy (H1, H2, H3)
- Include step-by-step instructions
- Add screenshots or examples when helpful
- End with a clear conclusion or next steps

## Editing Existing Resources

### Accessing Edit Mode
1. Go to **Resources** in admin panel
2. Find the resource in the list
3. Click **"Edit"** button
4. Make your changes
5. Click **"Save Changes"**

### Common Edits
- **Updating content**: Modify markdown or descriptions
- **Changing categories**: Add or remove category assignments
- **Status changes**: Publish drafts or unpublish content
- **URL updates**: Fix broken links or redirect to new locations

## Category Management

### Creating Categories
1. Go to **Categories** section in admin panel
2. Click **"Add Category"**
3. Enter category name and description
4. Choose parent category if creating subcategory
5. Set display order

### Organizing Categories
- Use hierarchical structure (parent/child relationships)
- Keep category names short and clear
- Avoid too many top-level categories
- Consider user mental models

### Category Best Practices
\`\`\`markdown
✅ Good Structure:
- Getting Started
  - Account Setup
  - First Steps
- Resources
  - Documentation
  - Tools
- Support
  - FAQs
  - Contact

❌ Poor Structure:
- Stuff
- More Stuff
- Other Things
- Random Category
\`\`\`

## Content Organization Strategies

### By User Journey
Organize resources to match how users actually work:
1. **Getting Started** → Account setup, first steps
2. **Learning** → Tutorials, guides, documentation
3. **Doing** → Tools, templates, checklists
4. **Support** → Help, troubleshooting, contact

### By Content Type
Group similar content together:
- **Guides**: Step-by-step tutorials
- **References**: Quick lookup information
- **Tools**: Interactive resources
- **Templates**: Reusable formats

### By Audience
Create content paths for different user types:
- **New Users**: Onboarding content
- **Regular Users**: Day-to-day resources
- **Power Users**: Advanced features and customization
- **Administrators**: Management and configuration

## Quality Assurance

### Before Publishing
- [ ] Test all links (external resources)
- [ ] Preview markdown formatting (guides)
- [ ] Check spelling and grammar
- [ ] Verify category assignments
- [ ] Ensure mobile responsiveness
- [ ] Test search functionality

### Content Review Checklist
- [ ] Title is clear and descriptive
- [ ] Description accurately summarizes content
- [ ] Categories are appropriate and helpful
- [ ] Content is up-to-date and accurate
- [ ] Links work and are relevant
- [ ] Formatting is consistent and readable

## Analytics and Tracking

### Resource Performance
Monitor how resources are being used:
- **View counts**: Most popular content
- **Click-through rates**: External link effectiveness
- **Search queries**: What users are looking for
- **User feedback**: Comments and ratings

### Optimization Opportunities
- Update popular content regularly
- Improve descriptions for low-performing resources
- Create content to fill gaps in search results
- Remove or update outdated resources

## Common Workflows

### Adding a New Tool Integration
1. **Research**: Understand the tool and its value
2. **Create Resource**: Add as external link
3. **Write Guide**: Create internal guide for setup/usage
4. **Link Resources**: Connect the guide to the tool link
5. **Categorize**: Assign to appropriate categories
6. **Test**: Verify all links and content work
7. **Publish**: Make available to users

### Updating Existing Content
1. **Identify Need**: User feedback, analytics, or regular review
2. **Plan Changes**: What needs updating and why
3. **Edit Content**: Make necessary modifications
4. **Review**: Check formatting, links, and accuracy
5. **Test**: Verify everything works correctly
6. **Publish**: Deploy updated content

### Managing Content Lifecycle
- **Creation**: New content development
- **Active**: Regular use and maintenance
- **Update**: Periodic content refresh
- **Archive**: Move outdated content
- **Retirement**: Remove obsolete resources

## Troubleshooting

### Common Issues

#### Broken Links
- Check URL formatting (include https://)
- Verify external sites are accessible
- Update redirects and moved content
- Test from different devices/browsers

#### Formatting Problems
- Preview markdown before saving
- Check for proper heading hierarchy
- Ensure table formatting is correct
- Test on mobile devices

#### Category Issues
- Avoid too many categories
- Use consistent naming conventions
- Don't create overly specific categories
- Consider user mental models

### Getting Help
- Check existing documentation first
- Review similar resources for formatting examples
- Test changes in preview mode
- Ask for feedback from other admins

## Best Practices Summary

### Content Creation
- Write for your audience
- Use clear, descriptive titles
- Provide helpful descriptions
- Include relevant categories
- Test everything before publishing

### Organization
- Use logical category structure
- Group related content together
- Consider user workflows
- Maintain consistent formatting

### Maintenance
- Regular content reviews
- Update outdated information
- Monitor performance metrics
- Gather user feedback
- Archive obsolete content

Remember: Good resource management is about making information findable, useful, and up-to-date for your users.`
    },
    'managing-users': {
      title: 'Managing Users and Accounts',
      content: `# Managing Users and Accounts

This guide covers how to create, edit, and manage user accounts, roles, and permissions in the IAS Portal admin panel. Learn how to handle user lifecycle from creation to deactivation.

## Understanding User Roles

### Role Hierarchy (4-Level System)
The IAS Portal uses a 4-level role hierarchy where users at higher levels have access to all content available to lower levels:

| Role | Hierarchy Level | Permissions | Use Case |
|------|----------------|-------------|----------|
| **Super Admin** | Special | Full system access, all admin functions, cannot be deactivated | System administrators (tim@warriorsage.com) |
| **Admin** | Level 4 | User management, content management, system monitoring | Content managers, user administrators |
| **Advisor** | Level 3 | Full content access (free), advisor features | Professional advisors, expert consultants |
| **Staff** | Level 3 | Full content access (free), same as advisors | Internal staff members |
| **Investor** | Level 3 | Full content access (free), same as advisors/staff | Investor members |
| **Paid Member** | Level 2 | Full content access (requires subscription) | Subscription members ($1k/month) |
| **Free Member** | Level 1 | Limited content access | Free tier members |

### Permission Matrix

| Function | Admin | Advisor/Staff/Investor | Paid Member | Free Member |
|----------|-------|------------------------|-------------|-------------|
| View Dashboard | ✅ | ✅ | ✅ | ✅ |
| Manage Users | ✅ | ❌ | ❌ | ❌ |
| Manage Content | ✅ | ❌ | ❌ | ❌ |
| View Resources | ✅ | ✅ | ✅ | ✅ (limited) |
| View Videos | ✅ | ✅ | ✅ | ✅ (limited) |
| Create Homework | ✅ | ❌ | ❌ | ❌ |
| Submit Homework | ✅ | ✅ | ✅ | ✅ |
| View Analytics | ✅ | ❌ | ❌ | ❌ |
| AI Prompts | ✅ | ✅ | ✅ | ✅ (limited) |
| Events | ✅ | ✅ | ✅ | ✅ |

### Role Access Details

#### **Admin (Level 4)**
- ✅ Full system management access
- ✅ User creation and management
- ✅ Content management (videos, resources, prompts, homework, events)
- ✅ Bulk user import tools
- ✅ Stripe integration management
- ✅ System analytics and monitoring

#### **Advisor, Staff, Investor (Level 3)**
- ✅ Full content access without subscription (free)
- ✅ All videos, resources, and prompts
- ✅ Homework and events access
- ✅ Created via invite email (no password during creation)
- ⚠️ Must complete password setup via email link before first login

#### **Paid Member (Level 2)**
- ✅ Full content access with active subscription
- ✅ Video library with Vimeo integration
- ✅ Resource downloads
- ✅ AI prompt library
- ✅ Homework assignments and events
- ✅ Synced with Stripe for subscription management

#### **Free Member (Level 1)**
- ✅ Limited content access
- ✅ Basic video library
- ✅ Limited resources
- ✅ Public AI prompts

## Creating New Users

### Step 1: Access User Management
1. Navigate to **Admin Dashboard**
2. Click on **"Users"** tab
3. Click **"Add User"** button

### Step 2: Basic Information
Fill in the required fields:

- **Email Address**: Primary identifier (required)
- **First Name**: User's first name (required)
- **Last Name**: User's last name (required)
- **Role**: Select appropriate role level
- **Status**: Active or Inactive (for advisors/staff/investors, starts as Inactive until password setup)

### Step 3: Account Setup Options
The setup process varies by role:

#### For Advisors, Staff, and Investors:
- **Automatically uses invite email system**
- No password fields shown during creation
- Account created as **Inactive** by default
- User receives invite email with password setup link
- Must complete password setup within 48 hours
- Account becomes Active after password setup

#### For Paid Members and Free Members:
Choose how the user will set up their account:

**Option A: Send Setup Email** (Recommended)
- Check "Send invite email" checkbox
- User receives email with setup link
- Must create password within 48 hours
- Account created as Inactive until password setup
- Most secure option

**Option B: Set Temporary Password**
- Leave "Send invite email" unchecked
- Admin creates initial password
- User can login immediately
- Password must meet security requirements (8+ chars with complexity)
- Use for urgent access needs

### Step 4: Additional Settings
- **Profile Completion**: Require profile completion on first login
- **Email Notifications**: Enable/disable notification preferences
- **Access Expiration**: Set account expiration date (optional)
- **Notes**: Internal notes about the user (not visible to user)

## User Account Lifecycle

### Account Creation Process
1. **Admin creates account** → System generates user record
2. **Setup email sent** → User receives account setup instructions
3. **User completes setup** → Password created, profile completed
4. **Account activated** → Full access granted based on role

### Account States
- **Pending**: Account created, setup not completed
- **Active**: Account fully set up and functional
- **Suspended**: Temporarily disabled by admin
- **Inactive**: Permanently disabled
- **Expired**: Account past expiration date

## Managing Existing Users

### Finding Users
Use the search and filter options:
- **Search by name or email**
- **Filter by role**: All Roles, Admin, Advisor, Staff, Investor, Paid Member, Free Member
- **Filter by status**: All Users, Active Users, Inactive Users
- **Sort by creation date, last login, etc.**

### Editing User Information
1. **Access user record** from Users list
2. **Click "Edit"** button
3. **Modify information** as needed:
   - Personal details (name, email)
   - Role assignments
   - Account status
   - Access permissions
4. **Save changes**

### Common User Management Tasks

#### Changing User Roles
\`\`\`markdown
1. Find the user in the Users list
2. Click "Edit" button
3. Change "Role" dropdown
4. Save changes
5. User will see new permissions on next login
\`\`\`

#### Resetting Passwords
\`\`\`markdown
1. Go to user's account page
2. Click "Reset Password" button
3. Choose reset method:
   - Send reset email (recommended)
   - Set temporary password
4. User receives instructions via email
\`\`\`

#### Suspending Accounts
\`\`\`markdown
1. Edit user account
2. Change status to "Suspended"
3. Add reason for suspension
4. User loses access immediately
5. Can be reactivated later
\`\`\`

## Bulk User Operations

The Admin Dashboard provides two powerful bulk import tools for adding multiple users at once.

### 1. Stripe Import Tool 💳

**Purpose:** Import existing Stripe subscribers who have paid subscriptions.

**Location:** Admin Dashboard → System Tools → Stripe Import

   **Key Features:**
   - Import from Stripe CSV export or JSON
   - Automatically links users to Stripe customer IDs
   - Sets users with member_paid role
   - Subscription management via webhooks

### 2. Bulk User Import Tool 👥

**Purpose:** Import advisors, investors, or free members who are NOT Stripe customers.

**Location:** Admin Dashboard → System Tools → Stripe Import (displayed below Stripe Import tool)

**Use Cases:**
- Adding advisors who don't have Stripe subscriptions
- Importing investors who have special access
- Bulk adding free members
- Adding staff members

### How to Use Bulk User Import

1. **Prepare Your CSV File:**
   - Open Excel, Google Sheets, or any spreadsheet tool
   - Create columns with the following headers (case-insensitive):
     - email (required)
     - role (required)
     - name OR first_name and last_name (at least one required)

2. **CSV Format Requirements:**

   **Required Columns:**
   - email - Valid email address (e.g., advisor@example.com)
   - role - Must be one of the following **exact values** (case-insensitive):
     - advisor - Professional advisors (Level 3, free access)
     - staff - Internal staff members (Level 3, free access)
     - investor - Investor members (Level 3, free access)
     - member_free - Free tier members (Level 1, limited access)
     - member_paid - Paid subscription members (Level 2, full access with subscription)
     - admin - Admin users (Level 4, full system access) ⚠️ Use with caution

   **Name Column Options (choose one):**
   - Option A: name - Full name (e.g., "John Doe") - will be split into first/last
   - Option B: first_name and last_name - Separate columns (e.g., "John" and "Doe")

3. **Example CSV File:**

   **Using name column:**
   \`\`\`csv
   email,role,name
   advisor1@example.com,advisor,"Jane Smith"
   investor1@example.com,investor,"Bob Johnson"
   freemember@example.com,member_free,"Alice Williams"
   \`\`\`

   **Using first_name and last_name columns:**
   \`\`\`csv
   email,role,first_name,last_name
   advisor1@example.com,advisor,Jane,Smith
   investor1@example.com,investor,Bob,Johnson
   freemember@example.com,member_free,Alice,Williams
   \`\`\`

4. **Import Process:**
   - Navigate to **Admin Dashboard** → **System Tools** → **Stripe Import**
   - Scroll down to the **"Bulk User Import"** section
   - Click **"CSV File Upload"** and select your CSV file
   - Review the preview (first 5 rows displayed)
   - Click **"Import Users"** button
   - Review the results (users created, skipped, errors)

### What Happens After Import

✅ **Users Created:**
- Users are created with the specified role
- No password is set (users must set password via invite email)
- No Stripe data is associated
- Users are active and ready to receive invite emails
- Email addresses must be unique

⚠️ **Duplicate Handling:**
- If a user with the same email already exists, they are **skipped** (not updated)
- The import will continue processing remaining users
- Check the results summary for skipped count

❌ **Error Handling:**
- Invalid email format → Error reported for that user
- Invalid role value → Error reported for that user
- Missing required columns → Import rejected with clear error message
- Missing name fields → Import rejected with clear error message

### Valid Role Values (Exact Spelling Required)

| Role Value | Hierarchy Level | Description | Access |
|------------|----------------|-------------|--------|
| admin | Level 4 | Admin access level | Full system access + all content |
| advisor | Level 3 | Advisor access level | Full content access (free) |
| staff | Level 3 | Staff access level | Full content access (free) |
| investor | Level 3 | Investor access level | Full content access (free) |
| member_paid | Level 2 | Paid member access | Full content access (paid subscription) |
| member_free | Level 1 | Free member access | Limited content access |

**⚠️ Important:** Role values are case-insensitive but must match exactly: advisor, investor, member_free, member_paid, staff, or admin

**Common mistakes:** 
- ❌ Advisor (should be advisor)
- ❌ Staff (should be staff) 
- ❌ Investor (should be investor)
- ❌ free (should be member_free)
- ❌ paid (should be member_paid)

### Post-Import Actions

📧 **Invite Emails:**
- After import, users need to be sent invite emails to set up their passwords
- Users cannot login until they set a password via invite link
- Use the "Send Invite" feature in the Users tab for individual users
- **Advisors, Staff, and Investors**: Automatically use invite system (no password during creation)
- **Free/Paid Members**: Can use invite or password creation (admin choice)

🔐 **No Stripe Data:**
- These users will NOT have Stripe customer IDs
- Subscription status will be set based on role:
  - **Advisors, Staff, Investors**: Automatically get active status (free access)
  - **Paid Members**: Start as inactive until subscription setup
  - **Free Members**: Start as active (free tier access)
- These users are NOT managed by Stripe webhooks

📊 **Audit Logging:**
- All bulk imports are logged in the audit log
- Includes number of users created, skipped, and errors
- Tracked with admin user ID and timestamp

### Troubleshooting Bulk Import

❌ **"CSV must contain an 'email' column"**
- Ensure your CSV has a column header exactly named email (case-insensitive)

❌ **"CSV must contain a 'role' column"**
- Ensure your CSV has a column header exactly named role (case-insensitive)

❌ **"Invalid role: [role]"**
- Check that role values match exactly: advisor, investor, member_free, member_paid, staff, admin
- Ensure no extra spaces or typos in role values

❌ **"CSV must contain either a 'name' column or both 'first_name' and 'last_name' columns"**
- Add a name column OR both first_name and last_name columns

❌ **"User already exists"**
- This is normal - existing users are skipped automatically
- Check the "Users skipped" count in results

### Other Bulk Actions
- **Send welcome emails** to multiple users
- **Change roles** for selected users
- **Export user data** for reporting
- **Deactivate accounts** in bulk

## User Communication

### Email Templates
The system includes several email templates:

#### Welcome Email
- Sent when account is created
- Includes setup instructions
- Contains login link
- Sets expectations for new users

#### Password Reset Email
- Sent when password is reset
- Includes secure reset link
- Expires after 24 hours
- Clear instructions for reset process

#### Account Status Changes
- Notifications for role changes
- Account suspension notices
- Access expiration warnings
- System maintenance alerts

### Customizing Communications
- **Email templates** can be modified in admin settings
- **Branding** can be customized
- **Frequency** of notifications can be adjusted
- **Content** can be tailored to your organization

## Security and Access Control

### Password Policies
- **Minimum length**: 8 characters
- **Complexity requirements**: Mix of letters, numbers, symbols
- **Expiration**: 90 days (configurable)
- **History**: Cannot reuse last 5 passwords
- **Lockout**: 5 failed attempts locks account for 15 minutes

### Session Management
- **Session timeout**: 8 hours of inactivity
- **Concurrent sessions**: Limited to 3 per user
- **Device tracking**: Monitor login locations
- **Logout**: Automatic on password change

### Access Logging
All user activities are logged:
- **Login/logout events**
- **Role changes**
- **Permission modifications**
- **Data access patterns**
- **Security events**

## Troubleshooting Common Issues

### User Cannot Login
1. **Check account status** - Is account active?
2. **Verify email address** - Correct spelling?
3. **Check password** - Recently changed?
4. **Review access logs** - Any security blocks?
5. **Reset password** if needed

### Email Delivery Issues
1. **Check spam folders** - Emails may be filtered
2. **Verify email address** - Correct format?
3. **Check email server** - Any delivery issues?
4. **Resend email** - Try sending again
5. **Use alternative email** - Different address

### Permission Problems
1. **Verify user role** - Correct assignment?
2. **Check role permissions** - What's allowed?
3. **Review access logs** - What was attempted?
4. **Update role** if needed
5. **Contact user** to confirm expectations

## Best Practices

### User Onboarding
- **Send welcome email** immediately after creation
- **Provide clear instructions** for account setup
- **Include contact information** for support
- **Set expectations** for portal usage
- **Follow up** after first login

### Role Management
- **Assign minimum necessary permissions**
- **Review roles regularly** for appropriateness
- **Document role changes** with reasons
- **Train users** on their capabilities
- **Monitor usage** for security

### Account Maintenance
- **Regular security reviews** of user accounts
- **Clean up inactive accounts** periodically
- **Update user information** as needed
- **Monitor access patterns** for anomalies
- **Archive old accounts** properly

### Communication
- **Be clear about changes** affecting users
- **Provide advance notice** for maintenance
- **Explain new features** and capabilities
- **Offer support** for questions
- **Gather feedback** on user experience

## Advanced Features

### User Groups
Organize users into groups for easier management:
- **Department groups** (Marketing, Sales, etc.)
- **Project groups** (Specific initiatives)
- **Access groups** (Similar permission needs)
- **Geographic groups** (Location-based)

### Custom Fields
Add custom information to user profiles:
- **Department** or **Team**
- **Manager** or **Supervisor**
- **Start date** or **Employment type**
- **Custom attributes** specific to your organization

### Integration Options
- **LDAP/Active Directory** integration
- **Single Sign-On (SSO)** setup
- **API access** for external systems
- **Webhook notifications** for events
- **Custom authentication** methods

## Reporting and Analytics

### User Activity Reports
- **Login frequency** and patterns
- **Feature usage** statistics
- **Content access** metrics
- **Performance** indicators
- **Engagement** levels

### Administrative Reports
- **User growth** over time
- **Role distribution** across organization
- **Account status** summaries
- **Security events** and alerts
- **System usage** trends

### Export Options
- **CSV exports** for spreadsheet analysis
- **PDF reports** for documentation
- **API access** for custom reporting
- **Scheduled reports** for regular updates
- **Custom dashboards** for real-time monitoring

## Conclusion

Effective user management is crucial for maintaining security, providing good user experience, and ensuring proper access control. Follow these guidelines to create a smooth user management process that serves both administrators and users effectively.

Remember to:
- **Plan user roles** carefully
- **Communicate clearly** with users
- **Monitor activity** regularly
- **Maintain security** standards
- **Provide support** when needed`
    }
  };

  useEffect(() => {
    loadGuide();
  }, [slug]);

  const loadGuide = async () => {
    try {
      setLoading(true);
      setError('');

      const guide = guideMetadata[slug];
      
      if (!guide) {
        setError('Guide not found');
        return;
      }

      // Use static content directly
      setGuideContent(guide.content);
      setGuideTitle(guide.title);
    } catch (error) {
      console.error('Error loading guide:', error);
      setError('Failed to load guide content');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
            <p className="text-gray-600 font-medium">Loading guide...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Guide Not Found</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/admin/guides')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Admin Guides
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-8 py-12">
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin/guides')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Admin Guides
            </button>
          </div>
          
          {/* Breadcrumb */}
          <div className="text-sm text-gray-500 mb-4">
            Admin Dashboard / Admin Guides / {guideTitle}
          </div>
          
          {/* Guide Title */}
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{guideTitle}</h1>
              <p className="text-gray-600 mt-1">Administrator Documentation</p>
            </div>
          </div>
          
          {/* Markdown Content */}
          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-4xl prose-h1:mb-4 prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:mb-4 prose-li:mb-2 prose-table:border-collapse prose-th:border prose-th:border-gray-300 prose-th:bg-gray-100 prose-th:p-3 prose-td:border prose-td:border-gray-300 prose-td:p-3 prose-strong:font-semibold prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  
                  if (!inline) {
                    // This is a code block (```)
                    return (
                      <div className="relative">
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                        <CopyButton textToCopy={String(children).replace(/\n$/, '')} />
                      </div>
                    );
                  }
                  
                  // This is inline code (`)
                  return (
                    <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {guideContent}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGuideViewer;
